import CheckIcon from "@mui/icons-material/Check";
import { Box } from "@mui/material";
import { useTranslate } from "react-admin";

import { CRM_COLORS } from "../../lib/crm";

type CrmPathProps = {
  stages: string[];
  current: string | null | undefined;
};

const CHEVRON_PX = 10;

// The Salesforce path: chevrons from the first stage to the last, done stages
// green with a check, the current one dark blue. A status outside the stages
// is shown as its own stage.
export const CrmPath = ({ stages, current }: CrmPathProps) => {
  const translate = useTranslate();
  const shown = current && !stages.includes(current) ? [...stages, current] : stages;
  const currentIndex = current ? shown.indexOf(current) : -1;

  return (
    <Box
      component="ol"
      aria-label={translate("crm.record.path")}
      sx={{ display: "flex", gap: "4px", listStyle: "none", m: 0, p: "0 16px 16px", overflowX: "auto" }}
    >
      {shown.map((stage, index) => {
        const done = currentIndex >= 0 && index < currentIndex;
        const isCurrent = index === currentIndex;
        const background = isCurrent
          ? CRM_COLORS.pathCurrent
          : done
            ? CRM_COLORS.pathComplete
            : CRM_COLORS.pathIncomplete;
        const label = translate(
          isCurrent ? "crm.record.pathCurrent" : done ? "crm.record.pathComplete" : "crm.record.pathUpcoming",
          { stage }
        );
        return (
          <Box
            component="li"
            key={stage}
            aria-current={isCurrent ? "step" : undefined}
            aria-label={label}
            title={label}
            sx={{
              flex: "1 1 0",
              minWidth: 96,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
              height: 32,
              px: `${CHEVRON_PX + 6}px`,
              backgroundColor: background,
              color: isCurrent || done ? CRM_COLORS.iconForeground : CRM_COLORS.pathIncompleteText,
              fontSize: "13px",
              fontWeight: isCurrent ? 700 : 400,
              whiteSpace: "nowrap",
              clipPath:
                index === 0
                  ? `polygon(0 0, calc(100% - ${CHEVRON_PX}px) 0, 100% 50%, calc(100% - ${CHEVRON_PX}px) 100%, 0 100%)`
                  : `polygon(0 0, calc(100% - ${CHEVRON_PX}px) 0, 100% 50%, calc(100% - ${CHEVRON_PX}px) 100%, 0 100%, ${CHEVRON_PX}px 50%)`,
              borderRadius: index === 0 ? "16px 0 0 16px" : undefined,
            }}
          >
            {done && <CheckIcon sx={{ fontSize: 16 }} />}
            {stage}
          </Box>
        );
      })}
    </Box>
  );
};
